import React, { useCallback, useEffect, useState } from 'react';
import { apiGet, apiPost, apiDelete } from '../../utils/backendApi';
import './MailSection.css';

// Email service on a hosted app (GET/POST /apps/:orgId/:appId/mail). Shared
// plane is included with the plan; the dedicated plane is an add-on. The
// section walks the admin through: turn on → publish DNS → mailboxes →
// reputation. Everything shown is server state (appInstance.mail).

const fmtUsd = (cents) => `$${((cents || 0) / 100).toFixed(0)}`;
const fmtGb = (bytes) => `${(((bytes || 0) / 1024 ** 3) || 0).toFixed(2)} GB`;
const STATUS_LABEL = {
  off: 'Off', verifying: 'Setting up', 'dns-pending': 'Waiting for DNS', 'awaiting-plane': 'Waiting for your mail server',
  live: 'Live', paused: 'Sending paused', suspended: 'Disabled',
};
const STATUS_TONE = { live: 'ok', paused: 'danger', 'dns-pending': 'warn', verifying: 'info', 'awaiting-plane': 'info', suspended: 'muted', off: 'muted' };
const REP_LABEL = { warmup: 'Warming up', good: 'Good standing', throttled: 'Throttled', paused: 'Paused' };

function CopyBtn({ value }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className="cs-btn cs-btn-sm cs-btn-outline ms-copy"
      onClick={async () => { try { await navigator.clipboard.writeText(value); setDone(true); setTimeout(() => setDone(false), 1200); } catch { /* ignore */ } }}
    >
      {done ? 'Copied' : 'Copy'}
    </button>
  );
}

export default function MailSection({ orgId, appId, app, isAdmin }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [newLocal, setNewLocal] = useState('');
  const [newName, setNewName] = useState('');

  const load = useCallback(async () => {
    try {
      const d = await apiGet(`/apps/${orgId}/${appId}/mail`);
      setData(d);
      setErr('');
    } catch (e) {
      setErr(e.message);
    }
  }, [orgId, appId]);

  const status = data?.mail?.status || 'off';
  const working = ['verifying'].includes(status) || (data?.job && ['queued', 'running'].includes(data.job.status));
  useEffect(() => {
    load();
    if (!working) return undefined;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [load, working]);

  const act = async (action, label) => {
    setBusy(action);
    setMsg('');
    try {
      await apiPost(`/apps/${orgId}/${appId}/mail`, { action });
      setMsg(label || 'Queued');
      await load();
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy('');
    }
  };

  const addMailbox = async (e) => {
    e.preventDefault();
    if (!newLocal.trim()) return;
    setBusy('mailbox');
    setMsg('');
    try {
      const r = await apiPost(`/apps/${orgId}/${appId}/mail/accounts`, { localPart: newLocal.trim(), displayName: newName.trim() });
      setMsg(`Created ${r.address} — the person opens /mail in the app to claim it.`);
      setNewLocal('');
      setNewName('');
      await load();
    } catch (e2) {
      setMsg(e2.message);
    } finally {
      setBusy('');
    }
  };

  const removeMailbox = async (address) => {
    if (!window.confirm(`Delete ${address} and all of its mail? This cannot be undone.`)) return;
    setBusy(address);
    try {
      await apiDelete(`/apps/${orgId}/${appId}/mail/accounts/${encodeURIComponent(address.split('@')[0])}`);
      await load();
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy('');
    }
  };

  if (err) return <p className="cs-error">{err}</p>;
  if (!data) return <p className="cs-help">Loading…</p>;

  const { mail = {}, eligibility: elig = {}, mailboxes = [], job, dedicatedAddon } = data;
  const rep = mail.reputation || {};
  const required = mail.dns?.required || [];
  const missing = new Set((mail.dns?.missing || []).map((m) => `${m.type}/${m.name}/${m.content}`));
  const tone = STATUS_TONE[status] || 'muted';

  return (
    <div className="ms">
      <div className="ms-head">
        <span className={`ms-badge is-${tone}`}>{STATUS_LABEL[status] || status}</span>
        <span className="ms-domain">{data.domain}</span>
        {mail.tier && <span className="cs-help">· {mail.tier === 'dedicated' ? 'dedicated mail server' : 'shared mail service'}</span>}
        {status === 'live' && rep.state && <span className={`ms-badge is-${rep.state === 'good' ? 'ok' : rep.state === 'warmup' ? 'info' : rep.state === 'throttled' ? 'warn' : 'danger'}`}>{REP_LABEL[rep.state] || rep.state}</span>}
      </div>

      {status === 'off' && (
        <div className="cs-card ms-card">
          <p>
            Mailboxes on <strong>{data.domain}</strong> and a Gmail-style inbox inside your app at <code>/mail</code>.
            {elig.shared
              ? ` Included with your plan: ${elig.includedMailboxes} mailboxes, ${elig.mailboxQuotaGb} GB each, on nventr's shared mail service.`
              : ' Included with Kit and Kit Business plans.'}
          </p>
          {!app?.customDomain && <p className="cs-help">Tip: set a custom domain first so addresses read <code>you@yourdomain.com</code> instead of <code>you@{data.domain}</code>. You can switch later — mail follows the domain.</p>}
          {isAdmin && elig.shared && (
            <button type="button" className="cs-btn cs-btn-primary" disabled={!!busy} onClick={() => act('enable', 'Setting up email…')}>
              Turn on email
            </button>
          )}
          {!elig.shared && <p className="cs-help">Upgrade the app's plan to add email.</p>}
        </div>
      )}

      {status === 'awaiting-plane' && (
        <div className="cs-card ms-card">
          <p>Your dedicated mail server is being set up by nventr — we will notify you when it is ready (typically within one business day). Nothing to do on your side yet.</p>
        </div>
      )}

      {['dns-pending', 'verifying', 'live', 'paused'].includes(status) && required.length > 0 && (
        <div className="cs-card ms-card">
          <h3>DNS records {status === 'live' ? <span className="ms-badge is-ok">all published</span> : <span className="ms-badge is-warn">{mail.dns?.missing?.length || 0} missing</span>}</h3>
          <p className="cs-help">
            Publish these at your DNS provider for <strong>{mail.domain}</strong>. MX receives mail; SPF, DKIM and DMARC prove your outbound is yours. Mail cannot go live until all four resolve.
          </p>
          <table className="ms-dns">
            <thead><tr><th>Type</th><th>Name</th><th>Value</th><th aria-label="state" /></tr></thead>
            <tbody>
              {required.filter((r) => ['MX', 'TXT', 'CNAME', 'SRV'].includes(r.type)).map((r) => {
                const miss = missing.has(`${r.type}/${r.name}/${r.content}`);
                const need = r.type === 'MX' || (r.type === 'TXT' && (/_domainkey|_dmarc/.test(r.name) || /^v=spf1/i.test(r.content)));
                return (
                  <tr key={`${r.type}/${r.name}/${r.content}`} className={miss ? 'is-missing' : ''}>
                    <td className="ms-mono">{r.type}</td>
                    <td className="ms-mono ms-break">{r.name}</td>
                    <td className="ms-mono ms-break">{r.content}</td>
                    <td className="ms-state">
                      {need ? (miss ? <span className="ms-badge is-warn">missing</span> : <span className="ms-badge is-ok">ok</span>) : <span className="cs-help">optional</span>}
                      <CopyBtn value={r.content} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {isAdmin && status !== 'live' && (
            <button type="button" className="cs-btn cs-btn-sm" disabled={!!busy} onClick={() => act('recheck-dns', 'Re-checking DNS…')}>Re-check DNS</button>
          )}
          {mail.dns?.lastCheckAt && <span className="cs-help ms-inline"> last checked {new Date(mail.dns.lastCheckAt).toLocaleString()}</span>}
        </div>
      )}

      {['live', 'paused'].includes(status) && (
        <div className="cs-card ms-card">
          <h3>Mailboxes <span className="cs-help">{mailboxes.length} of {elig.includedMailboxes} included · {elig.mailboxQuotaGb} GB each · extra mailboxes are metered</span></h3>
          {mail.planeError && <p className="cs-error">{mail.planeError}</p>}
          <table className="ms-boxes">
            <tbody>
              {mailboxes.map((m) => (
                <tr key={m.name}>
                  <td className="ms-mono">{m.name}</td>
                  <td className="cs-help">{m.description}</td>
                  <td className="cs-help">{fmtGb(m.usedQuota)} / {m.quota ? fmtGb(m.quota) : '∞'}</td>
                  <td className="ms-state">
                    {isAdmin && !/^postmaster@/.test(m.name) && (
                      <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" disabled={busy === m.name} onClick={() => removeMailbox(m.name)}>Delete</button>
                    )}
                  </td>
                </tr>
              ))}
              {!mailboxes.length && <tr><td className="cs-help" colSpan={4}>No mailboxes yet.</td></tr>}
            </tbody>
          </table>
          {isAdmin && (
            <form className="ms-add" onSubmit={addMailbox}>
              <input className="cs-input" placeholder="name" value={newLocal} onChange={(e) => setNewLocal(e.target.value.toLowerCase())} aria-label="mailbox name" />
              <span className="ms-at">@{mail.domain}</span>
              <input className="cs-input" placeholder="Display name (optional)" value={newName} onChange={(e) => setNewName(e.target.value)} aria-label="display name" />
              <button type="submit" className="cs-btn cs-btn-sm" disabled={busy === 'mailbox' || !newLocal.trim()}>Add mailbox</button>
            </form>
          )}
          <p className="cs-help">People claim a mailbox by opening <code>/mail</code> in the app with their app login — passwords never travel by email.</p>
        </div>
      )}

      {['live', 'paused'].includes(status) && (
        <div className="cs-card ms-card">
          <h3>Sending reputation <span className={`ms-badge is-${rep.state === 'good' ? 'ok' : rep.state === 'warmup' ? 'info' : rep.state === 'throttled' ? 'warn' : 'danger'}`}>{REP_LABEL[rep.state] || '—'}</span></h3>
          <div className="ms-stats">
            <div><span className="ms-stat">{rep.sendsToday ?? 0}</span><span className="cs-help">sent · 24h</span></div>
            <div><span className="ms-stat">{rep.bounces24h ?? 0}</span><span className="cs-help">bounced</span></div>
            <div><span className="ms-stat">{((rep.bounceRate || 0) * 100).toFixed(1)}%</span><span className="cs-help">bounce rate</span></div>
            <div><span className="ms-stat">{rep.cleanSends ?? 0}</span><span className="cs-help">clean sends total</span></div>
          </div>
          {rep.state === 'warmup' && <p className="cs-help">New domains send from a warm-up address with lower limits for about two weeks; once enough clean mail has gone out the domain graduates automatically.</p>}
          {rep.state === 'throttled' && <p className="cs-help">Bounce or complaint rate crossed the warning line — daily sending is halved until a clean day passes. {rep.lastReason}</p>}
          {rep.state === 'paused' && <p className="cs-error">Outbound is paused: {rep.lastReason || 'reviewed by nventr'}. Inbound still arrives. Contact support once the cause is fixed.</p>}
          <p className="cs-help">Limits: {elig.limits?.perMailboxPerHour}/hour and {elig.limits?.perMailboxPerDay}/day per mailbox, {elig.limits?.perDomainPerDay}/day for the domain, {elig.limits?.recipientsPerMessage} recipients per message, {elig.limits?.attachmentMb} MB attachments.</p>
        </div>
      )}

      {['live', 'paused', 'dns-pending'].includes(status) && mail.tier !== 'dedicated' && (
        <div className="cs-card ms-card ms-upgrade">
          <h3>{dedicatedAddon?.label || 'Dedicated mail server'} <span className="cs-help">+{fmtUsd(dedicatedAddon?.feeCents)}/mo</span></h3>
          <p className="cs-help">{dedicatedAddon?.blurb}</p>
          {isAdmin && (
            elig.dedicated
              ? <button type="button" className="cs-btn cs-btn-sm" disabled={!!busy} onClick={() => act('upgrade-dedicated', 'Dedicated server requested — we will set it up and move your domain.')}>Move to a dedicated server</button>
              : <span className="cs-help">{!elig.hasCustomDomain ? 'Needs your custom domain.' : 'Add the extra from Extras / billing, then come back here.'}</span>
          )}
        </div>
      )}

      {job && (
        <details className="ms-job">
          <summary className="cs-help">Last setup run · {job.action} · {job.status}{job.error ? ` · ${job.error}` : ''}</summary>
          <ul>
            {(job.stages || []).map((s) => <li key={s.name} className={`is-${s.status}`}><strong>{s.name}</strong> {s.status}{s.detail ? ` — ${s.detail}` : ''}</li>)}
          </ul>
        </details>
      )}

      {isAdmin && !['off', 'suspended'].includes(status) && (
        <p>
          <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" disabled={!!busy} onClick={() => { if (window.confirm('Turn off email? Mailboxes are kept for 30 days, then deleted.')) act('disable', 'Email turned off — mailboxes kept 30 days.'); }}>Turn off email</button>
        </p>
      )}
      {status === 'suspended' && (
        <p className="cs-help">Email is off. Mailboxes are kept until {mail.deleteAfter ? new Date(mail.deleteAfter).toLocaleDateString() : 'the retention date'}.{isAdmin && elig.shared && <> <button type="button" className="cs-btn cs-btn-sm" disabled={!!busy} onClick={() => act('enable', 'Turning email back on…')}>Turn back on</button></>}</p>
      )}
      {msg && <p className="cs-help ms-msg">{msg}</p>}
    </div>
  );
}
